import type { FC, ReactNode } from 'react';
import type { ClassNameValue } from 'tailwind-merge';

import { AlertCircle, CheckCircle, InfoCircle } from '@untitledui/icons';

import { Button } from '@/components/base/buttons/button';
import { CloseButton } from '@/components/base/buttons/close-button';
import { FeaturedIcon } from '@/components/foundations/featured-icon/featured-icon';
import { cx } from '@/utils/cx';

/**
 * Floating alert surfaces: every color is a borderless fill. `default` is the
 * plain white card, lifted off its background by a light shadow instead of
 * the outline it used to have, so it reads the same as the tinted colors.
 */
const floatingSurface = {
  default: 'bg-primary_alt',
  brand: 'bg-brand-primary',
  gray: 'bg-tertiary',
  error: 'bg-error-primary',
  warning: 'bg-warning-primary',
  success: 'bg-success-primary',
};

const iconMap = {
  default: InfoCircle,
  brand: InfoCircle,
  gray: InfoCircle,
  error: AlertCircle,
  warning: AlertCircle,
  success: CheckCircle,
};

interface AlertFloatingProps {
  /**
   * The title of the alert.
   */
  title: string;
  /**
   * The description of the alert. Optional: a one-line alert can carry its
   * whole message in `title` rather than padding it out with a second
   * sentence.
   */
  description?: ReactNode;
  /**
   * The label for the confirm button. Only rendered when `onConfirm` is set, so
   * a purely-informational alert (no actions) can omit it.
   */
  confirmLabel?: string;
  /**
   * The label for the dismiss button.
   * @default "Dismiss"
   */
  dismissLabel?: string;
  /**
   * The color of the alert.
   * @default "default"
   */
  color?: 'default' | 'brand' | 'gray' | 'error' | 'warning' | 'success';
  /**
   * The function to call when the dismiss button is clicked.
   */
  onClose?: () => void;
  /**
   * The function to call when the confirm button is clicked.
   */
  onConfirm?: () => void;
  /**
   * Extra actions, rendered after the dismiss and confirm buttons. For alerts
   * that offer more than a single confirm action.
   */
  actions?: ReactNode;
  className?: ClassNameValue;
}

export const AlertFloating = ({
  title,
  description,
  confirmLabel,
  onClose,
  onConfirm,
  actions,
  color = 'default',
  dismissLabel = 'Dismiss',
  className,
}: AlertFloatingProps) => {
  return (
    <div
      className={cx(
        'relative flex gap-4 rounded-xl p-4 md:flex-row',
        floatingSurface[color],
        color === 'default' && 'shadow-xs',
        className,
      )}
    >
      <FeaturedIcon
        icon={iconMap[color]}
        color={color === 'default' ? 'gray' : color}
        theme="outline"
        size="md"
      />

      <div className="flex flex-1 flex-col gap-3 md:w-0">
        <div className="flex flex-col gap-1 overflow-auto">
          <p className="pr-8 text-sm font-semibold text-secondary md:pr-0">
            {title}
          </p>
          {description && (
            <p className="text-sm text-tertiary">{description}</p>
          )}
        </div>

        {(onConfirm || onClose || actions) && (
          <div className="flex flex-wrap items-center justify-end gap-3">
            {onClose && (
              <Button onClick={onClose} size="sm" color="link-gray">
                {dismissLabel}
              </Button>
            )}
            {onConfirm && (
              <Button onClick={onConfirm} size="sm" color="link-color">
                {confirmLabel}
              </Button>
            )}
            {actions}
          </div>
        )}
      </div>

      {onClose && (
        <CloseButton
          onClick={onClose}
          size="sm"
          label={dismissLabel}
          className="absolute top-2 right-2"
        />
      )}
    </div>
  );
};

interface AlertFullWidthProps {
  /**
   * The title of the alert. A node is accepted so a live alert can crossfade
   * between two titles in place (see the screen-share banner).
   */
  title: ReactNode;
  /**
   * The description of the alert.
   */
  description: ReactNode;
  /**
   * The label for the confirm button. Only rendered when `onConfirm` is set, so
   * a purely-informational alert (no actions) can omit it.
   */
  confirmLabel?: string;
  /**
   * The label for the dismiss button.
   * @default "Dismiss"
   */
  dismissLabel?: string;
  /**
   * The type of the action buttons.
   * @default "button"
   */
  actionType?: 'button' | 'link';
  /**
   * The color of the alert.
   * @default "default"
   */
  color?: 'default' | 'brand' | 'gray' | 'error' | 'warning' | 'success';
  /**
   * The function to call when the dismiss button is clicked.
   */
  onClose?: () => void;
  /**
   * The function to call when the confirm button is clicked.
   */
  onConfirm?: () => void;
  /**
   * Whether the dismiss text button is shown when `onClose` is provided.
   * @default true
   */
  showDismissButton?: boolean;
  /**
   * Whether the corner "X" is offered alongside the dismiss button.
   * Pass `false` when `onClose` is a real action rather than a dismissal (e.g.
   * "Stop sharing"): an X that silently performs it reads as "hide this".
   * @default true
   */
  showCloseButton?: boolean;

  className?: ClassNameValue;
  icon?: FC<{ className?: string }> | ReactNode;
}

export const AlertFullWidth = ({
  title,
  description,
  confirmLabel,
  onClose,
  onConfirm,
  color = 'default',
  actionType = 'button',
  dismissLabel = 'Dismiss',
  showDismissButton = true,
  showCloseButton = true,
  className,
  icon,
}: AlertFullWidthProps) => {
  return (
    <div
      className={cx(
        'relative border-t border-primary bg-secondary md:border-t-0 md:border-b',
        className,
      )}
    >
      <div className="mx-auto flex max-w-container flex-col gap-4 p-4 md:flex-row md:items-center md:gap-3 md:px-8 md:py-3">
        <div className="flex flex-1 flex-col gap-4 md:w-0 md:flex-row md:items-center">
          <FeaturedIcon
            className="hidden md:flex"
            icon={icon ?? iconMap[color]}
            color={color === 'default' ? 'gray' : color}
            theme={color === 'default' ? 'modern' : 'outline'}
            size="md"
          />

          <div className="flex flex-col flex-wrap gap-0.5 overflow-hidden lg:flex-row lg:gap-1.5">
            <p className="pr-8 text-sm font-semibold text-secondary md:truncate md:pr-0">
              {title}
            </p>
            <p className="text-sm text-tertiary">{description}</p>
          </div>
        </div>

        {(onConfirm || onClose) && (
          <div className="flex gap-2">
            <div
              className={cx(
                'flex w-full gap-3',
                actionType === 'button'
                  ? 'flex-col-reverse md:flex-row'
                  : 'flex-row',
              )}
            >
              {onClose && showDismissButton && (
                <Button
                  onClick={onClose}
                  color={actionType === 'button' ? 'secondary' : 'link-gray'}
                  size="sm"
                >
                  {dismissLabel}
                </Button>
              )}
              {onConfirm && (
                <Button
                  onClick={onConfirm}
                  color={actionType === 'button' ? 'primary' : 'link-color'}
                  size="sm"
                >
                  {confirmLabel}
                </Button>
              )}
            </div>

            {onClose && showCloseButton && (
              <CloseButton
                onClick={onClose}
                size="sm"
                label={dismissLabel}
                className="absolute top-2 right-2 shrink-0 md:static"
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};
